import React, { forwardRef } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Pagination as PrimitivePagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "src/primitives/Pagination";

import { DOTS } from "./constants";
import { usePaginationQueryParams } from "./hooks/usePaginationQueryParams";
import { usePaginationRange } from "./hooks/usePaginationRange";

export interface PaginationProps extends Omit<
  React.ComponentProps<typeof PrimitivePagination>,
  "children"
> {
  /** Total number of items. */
  count: number;
  /** Current page number (1-based). */
  pageNo: number;
  /** Number of items per page. */
  pageSize: number;
  /** Number of sibling pages to show around the current page. */
  siblingCount?: number;
  /**
   * Callback when a page is selected. When omitted, Pagination falls back
   * to updating `?page=` in the URL via react-router history (matches v1
   * neeto-ui behavior).
   */
  navigate?: (page: number) => void;
  /** Additional CSS class names. */
  className?: string;
}

const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      count,
      pageNo,
      pageSize,
      siblingCount = 1,
      navigate,
      className,
      ...otherProps
    },
    ref
  ) => {
    const paginationRange = usePaginationRange({
      count,
      pageSize,
      siblingCount,
      pageNo,
    });
    const { updatePageInQueryParam } = usePaginationQueryParams();

    const lastPage = paginationRange[paginationRange.length - 1] as number;
    const isFirstPage = pageNo === 1;
    const isLastPage = pageNo === lastPage;

    // Don't render if only 1 page or no pages
    if (pageNo === 0 || paginationRange.length < 2) {
      return null;
    }

    const handleNavigate = navigate ?? updatePageInQueryParam;

    return (
      <PrimitivePagination ref={ref} className={cn(className)} {...otherProps}>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => !isFirstPage && handleNavigate(pageNo - 1)}
              aria-disabled={isFirstPage}
              className={cn(isFirstPage && "pointer-events-none opacity-50")}
            />
          </PaginationItem>

          {paginationRange.map((page, index) => (
            <PaginationItem key={`${page}-${index}`}>
              {page === DOTS ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  isActive={page === pageNo}
                  onClick={() => handleNavigate(page as number)}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              onClick={() => !isLastPage && handleNavigate(pageNo + 1)}
              aria-disabled={isLastPage}
              className={cn(isLastPage && "pointer-events-none opacity-50")}
            />
          </PaginationItem>
        </PaginationContent>
      </PrimitivePagination>
    );
  }
);

Pagination.displayName = "Pagination";

export { Pagination };
